import './lib/i18n'
import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryProvider } from './lib/react-query'
import './index.css'
import App from './App.tsx'

// PHI field patterns — keys whose values must never reach Sentry
const PHI_KEYS = /name|email|phone|dob|birth|address|patient|tc|ssn|nhs|national|identity|passport|insurance|diagnosis|symptom|note|complaint|treatment|medication|prescription|allergy/i;

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (PHI_KEYS.test(k)) {
      result[k] = '[Filtered]';
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      result[k] = scrubObject(v as Record<string, unknown>);
    } else {
      result[k] = v;
    }
  }
  return result;
}

// After signInWithRedirect on mobile, the page fully reloads. If a new
// deployment happened during the OAuth flow, old JS chunk URLs no longer
// exist and Firebase Hosting's SPA rewrite returns index.html (text/html)
// instead of JavaScript. Reload once so the fresh index.html fetches the
// correct chunks.
window.addEventListener('vite:preloadError', () => {
  window.location.reload()
})

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
  tracesSampleRate: 0.2,
  beforeSend(event) {
    // Drop noisy client-side errors that aren't actionable
    const msg = event.exception?.values?.[0]?.value ?? '';
    if (/ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module/i.test(msg)) return null;
    if (/is not a valid JavaScript MIME type/i.test(msg)) return null;
    if (/Network request failed|NetworkError|Load failed/i.test(msg) && !navigator.onLine) return null;

    // Scrub PHI from extra / contexts
    if (event.extra) event.extra = scrubObject(event.extra as Record<string, unknown>);
    if (event.user) {
      // Keep only safe user fields
      event.user = { id: event.user.id };
    }
    return event;
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryProvider>
      <App />
    </QueryProvider>
  </StrictMode>,
)
